import { useState, useCallback, useEffect } from 'react';
import { getCredentials } from '../CredentialsServices';

/**
 * Custom hook to handle credential fetching, pagination, search, and filters
 * @param {Function} handleError - Error handler function
 * @returns {Object} Credentials data and fetch functions
 */
const useCredentialFetching = (handleError) => {
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: [5, 10, 25, 50, 100],
    });
    const [searchString, setSearchString] = useState('');
    const [filters, setFilters] = useState([]);  // Array for SharedFilterBar
    const [normalizedFilters, setNormalizedFilters] = useState({});  // Object for API
    const [sorter, setSorter] = useState({
        field: null,
        order: null,
    });

    const fetchCredentials = useCallback(async (page, pageSize, search, filterParams, sortField, sortOrder) => {
        const isInitial = page === 1 && !credentials.length;
        if (isInitial) {
            setIsInitialLoading(true);
        }
        setLoading(true);

        try {
            const response = await getCredentials({
                page: page || pagination.current,
                size: pageSize || pagination.pageSize,
                search: search !== undefined ? search : searchString,
                filters: filterParams !== undefined ? filterParams : normalizedFilters,
                sort_by: sortField !== undefined ? sortField : sorter.field,
                sort_direction: sortOrder !== undefined ? sortOrder : sorter.order,
            });

            setCredentials(response.data || []);
            setPagination(prev => ({
                ...prev,
                current: response.metadata?.page || page || prev.current,
                pageSize: response.metadata?.page_size || pageSize || prev.pageSize,
                total: response.metadata?.total_count || 0,
            }));
        } catch (error) {
            handleError(error);
            setCredentials([]);
        } finally {
            setLoading(false);
            if (isInitial) {
                setIsInitialLoading(false);
            }
        }
    }, [handleError, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order, credentials.length]);

    const refetchCredentials = useCallback(() => {
        fetchCredentials(pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order);
    }, [fetchCredentials, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order]);

    const handleTableChange = useCallback((newPagination, tableFilters, tableSorter) => {
        const sortField = tableSorter.field || null;
        const sortOrder = tableSorter.order === 'ascend' ? 'asc' : tableSorter.order === 'descend' ? 'desc' : null;

        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        setSorter({
            field: sortField,
            order: sortOrder,
        });

        fetchCredentials(newPagination.current, newPagination.pageSize, searchString, normalizedFilters, sortField, sortOrder);
    }, [fetchCredentials, searchString, normalizedFilters]);

    const handleSearch = useCallback((value) => {
        setSearchString(value);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchCredentials(1, pagination.pageSize, value, normalizedFilters, sorter.field, sorter.order);
    }, [fetchCredentials, pagination.pageSize, normalizedFilters, sorter.field, sorter.order]);

    const handleFilterChange = useCallback((filtersArray, normalizedFiltersObject) => {
        setFilters(filtersArray);  // Store the array for SharedFilterBar
        setNormalizedFilters(normalizedFiltersObject);  // Store the object for API
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchCredentials(1, pagination.pageSize, searchString, normalizedFiltersObject, sorter.field, sorter.order);
    }, [fetchCredentials, pagination.pageSize, searchString, sorter.field, sorter.order]);

    // Initial fetch
    useEffect(() => {
        fetchCredentials(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        credentials,
        setCredentials,
        loading,
        isInitialLoading,
        pagination,
        searchString,
        filters,
        sorter,
        fetchCredentials,
        refetchCredentials,
        handleTableChange,
        handleSearch,
        handleFilterChange,
    };
};

export default useCredentialFetching;
